import React from 'react';
import { FormControl, InputLabel, OutlinedInput, styled } from '@mui/material';

interface ReadOnlyFieldProps {
  label: string;
  value: string | number | boolean;
  /**
   * Optional function to format the display value
   */
  formatValue?: (value: string | number | boolean) => string;
  /**
   * Optional fullWidth prop to match TextField API
   */
  fullWidth?: boolean;
}

const ReadOnlyFormControl = styled(FormControl)(({ theme }) => ({
  '& .MuiInputLabel-root': {
    color: theme.palette.text.primary, // Normal color for label
    // Remove the background color to make it transparent
  },
  '& .MuiInputLabel-shrink': {
    // Only apply transform, keeping background transparent
    transform: 'translate(14px, -9px) scale(0.75)',
  },
  '& .MuiOutlinedInput-root': {
    backgroundColor: theme.palette.action.hover, // Light grey background
    '& .MuiOutlinedInput-input': {
      color: theme.palette.text.primary, // Normal color for input text
      cursor: 'default',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(0, 0, 0, 0.23)', // Normal border color
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(0, 0, 0, 0.23)', // Prevent hover effect
    },
    // Remove focus styles completely
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      border: '1px solid rgba(0, 0, 0, 0.23)', // Keep normal border even when focused
      boxShadow: 'none',
    },
  },
}));

const ReadOnlyField: React.FC<ReadOnlyFieldProps> = ({
  label,
  value,
  formatValue = val => String(val),
  fullWidth = true,
}) => {
  const displayValue = formatValue(value);

  return (
    <ReadOnlyFormControl variant="outlined" fullWidth={fullWidth}>
      {/* Force shrink to ensure label is always on the border */}
      <InputLabel shrink>{label}</InputLabel>
      <OutlinedInput
        label={label}
        value={displayValue}
        notched
        readOnly
        tabIndex={-1}
        inputProps={{
          tabIndex: -1,
          'aria-readonly': true,
        }}
        sx={{
          '& .MuiOutlinedInput-input': {
            userSelect: 'text', // Allow text selection
          },
          // Remove outline on focus
          '&:focus, &:focus-within': {
            outline: 'none',
          },
          // Ensure the component doesn't show focus states
          '&.Mui-focused': {
            outline: 'none',
          },
        }}
      />
    </ReadOnlyFormControl>
  );
};

export default ReadOnlyField;
